import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { all_routes } from '../../router/all_routes';
import Table from "../../../core/common/dataTable/index";
import { DatePicker, Drawer, Input, message, Spin, TimePicker } from 'antd';
import { getEmployees } from '../../../core/data/redux/employee/employeeAction';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../core/data/redux/store';
import { fetchAllDepartments } from '../../../core/data/redux/department/departmentAction';
import { createShiftSchedule, deleteShiftSchedule, fetchAllShiftSchedules, updateShiftSchedule } from '../../../core/data/redux/attendance/shiftSchedule/shiftScheduleAction';
import dayjs, { Dayjs } from 'dayjs';
import Select from 'react-select';
import DeleteModal1 from '../../../core/modals/deleteModal1';
import { exportToCSV, exportToExcel, exportToPDF } from '../../../core/common/GlobalFunctions/exportTableData';
import { filterByStatus } from '../../../core/utils/filterActiveData';
import { fetchAllShiftMasters } from '../../../core/data/redux/attendance/shift/shiftMasterAction';
import { formatDate } from '../../../core/utils/configFormatters';
import { useAppConfig } from '../../../customhooks/useAppConfig';
import ShiftRosterDrawer from './rosterShiftSchedule';
import { decryptData } from '../../../core/utils/encryptUtils';
import ShiftCalendar from './ShiftCalendar';
import type { RangePickerProps } from 'antd/es/date-picker';
import { fetchAllHolidays } from '../../../core/data/redux/holiday/holidayAction';
const { RangePicker } = DatePicker;


type EmployeeOption = {
  value: string;
  label: string;
};
const optionLabel = (s: any) => `${s.ShiftName} (${s.StartTime} - ${s.EndTime})`;

const ScheduleTiming = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const { employees } = useSelector((state: any) => state.employee);
  const { departments } = useSelector((state: any) => state.department);
  const { shiftSchedules, loadingStates } = useSelector((state: any) => state.attendance.shiftSchedule);
  const isLoading = modalType === "edit" ? loadingStates.update : loadingStates.create;
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const { shiftMasters = [] } = useSelector((state: any) => state.attendance.shift);
  const [tableKey, setTableKey] = useState(0);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const activeDepartment = filterByStatus(departments, 'active', 'Status');
  const activeEmployee = filterByStatus(employees, 'active', 'employee_status');
  const [showRosterDrawer, setShowRosterDrawer] = useState(false);
  const [rosterLoading, setRosterLoading] = useState<boolean>(false);
  const [calendarDeptId, setCalendarDeptId] = useState<number | 'all'>('all');
  const [empQuery, setEmpQuery] = useState<string>('');
  const { holidays = [], loading } = useSelector((state: any) => state.holiday);
  const [isDepartmentLocked, setIsDepartmentLocked] = useState(false);


  // Start: Rotation drawer state
  // rotation drawer state (updated)
  const [showRotationDrawer, setShowRotationDrawer] = useState(false);
  type RotationPair = { fromShiftId?: string; toShiftId?: string; id: string };

  const [rotationFrequency, setRotationFrequency] = useState<'Weekly' | 'Monthly' | 'Daily'>('Weekly');
  const [rotationStartDate, setRotationStartDate] = useState<Dayjs | null>(dayjs()); // "From Day"
  const [rotationDeptIds, setRotationDeptIds] = useState<number[]>([]);
  const [rotationEmployeeMulti, setRotationEmployeeMulti] = useState<EmployeeOption[]>([]);
  const [rotationPairs, setRotationPairs] = useState<RotationPair[]>([{ id: crypto.randomUUID() }, { id: crypto.randomUUID() }]); // usually 2 pairs
  const [rotating, setRotating] = useState<boolean>(false);

  // helpers
  const getShiftById = (id?: string) => shiftMasters.find((s: any) => String(s.ShiftID) === String(id));

  const buildSchedulePayload = (targetShift: any, employeeIds: number[], dateRange: { from: Dayjs; to: Dayjs }, deptId: number) => ({
    EmployeeIDs: JSON.stringify(employeeIds),
    DepartmentID: deptId,
    ShiftName: targetShift?.ShiftName ?? '',
    fromDate: dateRange.from.format('YYYY-MM-DD'),
    toDate: dateRange.to.format('YYYY-MM-DD'),
    ShiftID: String(targetShift?.ShiftID ?? ''),
    MinStartTime: dayjs(`1970-01-01T${targetShift?.StartTime}`).subtract(1, 'hour').format('HH:mm:ss'),
    StartTime: targetShift?.StartTime ?? '09:00:00',
    MaxStartTime: dayjs(`1970-01-01T${targetShift?.StartTime}`).add(1, 'hour').format('HH:mm:ss'),
    MinEndTime: dayjs(`1970-01-01T${targetShift?.EndTime}`).subtract(1, 'hour').format('HH:mm:ss'),
    EndTime: targetShift?.EndTime ?? '18:00:00',
    MaxEndTime: dayjs(`1970-01-01T${targetShift?.EndTime}`).add(1, 'hour').format('HH:mm:ss'),
    BreakTime: targetShift?.BreakTime ?? '00:30:00',
    AcceptExtraHours: false,
    IsPublished: false,
  });

  // PERIODS: 4 cycles, alternating pair1/pair2/pair1/pair2
  const makeWeeklyPeriods = (start: Dayjs) => {
    const s = start.startOf('day');
    return [
      { from: s, to: s.add(6, 'day').endOf('day') },
      { from: s.add(7, 'day'), to: s.add(13, 'day').endOf('day') },
      { from: s.add(14, 'day'), to: s.add(20, 'day').endOf('day') },
      { from: s.add(21, 'day'), to: s.add(27, 'day').endOf('day') },
    ];
  };

  const makeMonthlyPeriods = (start: Dayjs) => {
    const m0 = start.startOf('month');
    return [
      { from: start.startOf('day'), to: start.endOf('month') },
      { from: m0.add(1, 'month').startOf('month'), to: m0.add(1, 'month').endOf('month') },
      { from: m0.add(2, 'month').startOf('month'), to: m0.add(2, 'month').endOf('month') },
      { from: m0.add(3, 'month').startOf('month'), to: m0.add(3, 'month').endOf('month') },
    ];
  };

  const makeDailyPeriods = (start: Dayjs) => {
    const s = start.startOf('day');
    return [
      { from: s, to: s.endOf('day') },
      { from: s.add(1, 'day'), to: s.add(1, 'day').endOf('day') },
      { from: s.add(2, 'day'), to: s.add(2, 'day').endOf('day') },
      { from: s.add(3, 'day'), to: s.add(3, 'day').endOf('day') },
    ];
  };


  const resetRotationForm = () => {
    setRotationFrequency('Weekly');
    setRotationStartDate(dayjs());         // today's date
    setRotationDeptIds([]);
    setRotationEmployeeMulti([]);
    setRotationPairs([{ id: crypto.randomUUID() }, { id: crypto.randomUUID() }]); // two empty rows
    setRotating(false);
  };

  const handleRotationClose = () => {
    resetRotationForm();
    setShowRotationDrawer(false);
  };

  // END: Rotation drawer state

  const filteredEmployeesForCalendar = useMemo(() => {
    let list = activeEmployee;

    // Department filter
    if (calendarDeptId !== 'all') {
      list = list.filter((e: any) => Number(e?.DepartmentId) === Number(calendarDeptId));
    }

    // Text search on Name or EmployeeNumber
    const q = empQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((e: any) => {
        const name = String(e?.Name ?? '').toLowerCase();
        const empNo = String(e?.EmployeeNumber ?? '').toLowerCase();
        return name.includes(q) || empNo.includes(q);
      });
    }

    return list;
  }, [activeEmployee, calendarDeptId, empQuery]);

  const [calendarRange, setCalendarRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('day'),
    dayjs().startOf('day').add(6, 'day'),
  ]);

  // Permission
  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");

  const [shiftScheduleData, setShiftScheduleData] = useState<{
    selectEmployeeNames: EmployeeOption[];
    departmentName: string;
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
    selectedShift: { value: string; label: string } | null;
    minStartTime: Dayjs | null;
    startTime: Dayjs | null;
    maxStartTime: Dayjs | null;
    minEndTime: Dayjs | null;
    endTime: Dayjs | null;
    maxEndTime: Dayjs | null;
    breakTime: Dayjs | null;
    acceptExtraHours: boolean;
    isPublished: boolean;
  }>({
    selectEmployeeNames: [],
    departmentName: "",
    fromDate: null,
    toDate: null,
    selectedShift: null,
    minStartTime: null,
    startTime: null,
    maxStartTime: null,
    minEndTime: null,
    endTime: null,
    maxEndTime: null,
    breakTime: null,
    acceptExtraHours: false,
    isPublished: false,
  });

  const resetFields = () => {
    setShiftScheduleData({
      selectEmployeeNames: [],
      departmentName: "",
      fromDate: null,
      toDate: null,
      selectedShift: null,
      minStartTime: null,
      startTime: null,
      maxStartTime: null,
      minEndTime: null,
      endTime: null,
      maxEndTime: null,
      breakTime: null,
      acceptExtraHours: false,
      isPublished: false,
    });
    setValidationErrors({});
  };

  const getAssignedEmployeeIDs = () => {
    if (!shiftSchedules.length || !shiftScheduleData.fromDate || !shiftScheduleData.toDate) return [];

    return shiftSchedules
      .filter((schedule: any) => {
        const scheduleFrom = dayjs(schedule.FromDate);
        const scheduleTo = dayjs(schedule.ToDate);

        return (
          scheduleFrom.isBefore(shiftScheduleData.toDate, "day") &&
          scheduleTo.isAfter(shiftScheduleData.fromDate, "day")
        );
      })
      .flatMap((schedule: any) => schedule.EmployeeIDs || []);
  };
  const assignedEmployeeIDs = getAssignedEmployeeIDs();

  useEffect(() => {
    if (!employees.length) {
      dispatch(getEmployees());
    }
  }, [employees.length, dispatch]);

  useEffect(() => {
    if (!departments.length) {
      dispatch(fetchAllDepartments());
    }
  }, [departments.length, dispatch]);

  useEffect(() => {
    if (!shiftSchedules.length) {
      dispatch(fetchAllShiftSchedules());
    }
  }, [shiftSchedules.length, dispatch]);

  useEffect(() => {
    dispatch(fetchAllShiftMasters());
  }, [dispatch]);

  useEffect(() => {
    if (!holidays.length) {
      dispatch(fetchAllHolidays());
    }
  }, [dispatch, holidays.length]);


 const showModal = (type: 'edit' | 'create') => {
  setModalType(type);
  setIsModalVisible(true);
  setIsDepartmentLocked(type === 'edit');
};

  const handleCancel = () => {
    setIsModalVisible(false);
    setModalType('');
    resetFields();
    setValidationErrors({});
  };
  const handleEditSchedule = (record: any): void => {
    setIsDepartmentLocked(true);
    const selectEmployeeNames = record.EmployeeNames && record.EmployeeIDs
      ? record.EmployeeIDs.map((id: any, index: number) => ({
        value: id,
        label: record.EmployeeNames[index],
      }))
      : [];

    let matched = shiftMasters.find((s: any) => String(s.ShiftName).trim() === String(record.ShiftName).trim());

    // 2) Fallback: match by exact times if names differ / duplicates exist
    if (!matched) {
      matched = shiftMasters.find(
        (s: any) =>
          String(s.StartTime) === String(record.StartTime) &&
          String(s.EndTime) === String(record.EndTime)
      );
    }

    const selectedShift =
      matched
        ? { value: String(matched.ShiftID), label: optionLabel(matched) }
        : null;


    setSelectedRowData(record);
    setShiftScheduleData({
      selectEmployeeNames: selectEmployeeNames,
      departmentName: record.DepartmentID,
      fromDate: record.FromDate ? dayjs(record.FromDate) : null,
      toDate: record.ToDate ? dayjs(record.ToDate) : null,
      selectedShift,
      minStartTime: record.MinStartTime ? dayjs(record.MinStartTime, "HH:mm:ss") : null,  // Convert time to Dayjs
      startTime: record.StartTime ? dayjs(record.StartTime, "HH:mm:ss") : null,  // Convert time to Dayjs
      maxStartTime: record.MaxStartTime ? dayjs(record.MaxStartTime, "HH:mm:ss") : null,  // Convert time to Dayjs
      minEndTime: record.MinEndTime ? dayjs(record.MinEndTime, "HH:mm:ss") : null,  // Convert time to Dayjs
      endTime: record.EndTime ? dayjs(record.EndTime, "HH:mm:ss") : null,  // Convert time to Dayjs
      maxEndTime: record.MaxEndTime ? dayjs(record.MaxEndTime, "HH:mm:ss") : null,  // Convert time to Dayjs
      breakTime: record.BreakTime ? dayjs(record.BreakTime, "HH:mm:ss") : null,  // Convert time to Dayjs
      acceptExtraHours: record.AcceptExtraHours === 1,  // Convert to boolean
      isPublished: record.Publish !== null ? record.Publish : false,  // Default to false if Publish is null
    });

    showModal('edit');
  };

  const handleInputChange = (field: string, value: any) => {
    if (field === 'selectedShift') {
      const selectedShift = shiftMasters.find((shift: any) => shift.ShiftID.toString() === value.value);
      if (selectedShift) {
        const start = dayjs(`1970-01-01T${selectedShift.StartTime}`);
        const end = dayjs(`1970-01-01T${selectedShift.EndTime}`);
        const breakTime = dayjs(`1970-01-01T${selectedShift.BreakTime}`);

        setShiftScheduleData(prev => ({
          ...prev,
          selectedShift: value,
          startTime: start,
          endTime: end,
          breakTime: breakTime,
          minStartTime: start.subtract(1, 'hour'),
          maxStartTime: start.add(1, 'hour'),
          minEndTime: end.subtract(1, 'hour'),
          maxEndTime: end.add(1, 'hour'),
        }));
        return;
      }
    }

    setShiftScheduleData(prevState => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleCalendarRangeChange = (values: (Dayjs | null)[]) => {
    const [rawStart, rawEnd] = values;
    if (!rawStart) {
      // reset to default week if user clears (but we keep allowClear=false anyway)
      const s = dayjs().startOf('day');
      setCalendarRange([s, s.add(6, 'day')]);
      return;
    }

    const start = rawStart.startOf('day');
    const endCandidate = (rawEnd ?? rawStart).endOf('day');

    // inclusive span in days
    const span = endCandidate.diff(start, 'day') + 1;

    if (span > 7) {
      message.warning('Please select a range of at most 7 days. Limiting to 7.');
      setCalendarRange([start, start.add(6, 'day')]);
    } else {
      setCalendarRange([start, endCandidate]);
    }
  };
  const openCreatePrefilledFor = (emp: any, date: Dayjs) => {
    setModalType("create");
    setIsDepartmentLocked(true);

    setShiftScheduleData((prev) => ({
      ...prev,
      departmentName: emp?.DepartmentId ? String(emp.DepartmentId) : "",
      selectEmployeeNames: emp?.EmployeeID
        ? [{ value: emp.EmployeeID, label: emp.Name }]
        : [],
      fromDate: date.startOf("day"),
      toDate: date.endOf("day"),
      selectedShift: null,
      minStartTime: null,
      startTime: null,
      maxStartTime: null,
      minEndTime: null,
      endTime: null,
      maxEndTime: null,
      breakTime: null,
      acceptExtraHours: false,
      isPublished: false,
    }));

    setIsModalVisible(true);
  };

  const handleCalendarCellClick = ({employee,date,schedule}: {employee: any;date: Dayjs;schedule: any | null;}) => {
    setIsDepartmentLocked(true);
    if (schedule) {
      handleEditSchedule(schedule);
    } else {
      openCreatePrefilledFor(employee, date);
    }
  };

  // Optional: quick next/prev week
  const shiftWeek = (dir: 1 | -1) => {
    const newStart = calendarRange[0].add(dir * 7, 'day').startOf('day');
    setCalendarRange([newStart, newStart.add(6, 'day')]);
  };

  // Optional: disable dates to guide selection (keeps it reasonable but not required)
  const disabledRangeDate: RangePickerProps['disabledDate'] = () => false; // k

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const errors: any = {};

    if (!shiftScheduleData.departmentName) {
      errors.departmentName = 'Department is required';
    }

    if (!shiftScheduleData.selectEmployeeNames || shiftScheduleData.selectEmployeeNames.length === 0) {
      errors.selectEmployeeNames = 'Employee(s) is required';
    }

    if (!shiftScheduleData.fromDate) {
      errors.fromDate = 'From Date is required';
    }
    if (!shiftScheduleData.toDate) {
      errors.toDate = 'To Date is required';
    }
    if (
      shiftScheduleData.fromDate &&
      shiftScheduleData.toDate &&
      shiftScheduleData.fromDate.isAfter(shiftScheduleData.toDate)
    ) {
      errors.toDate = 'To Date must be after From Date';
    }
    if (!shiftScheduleData.selectedShift) {
      errors.selectedShift = 'Shift is required';
    }

    if (!shiftScheduleData.minStartTime) {
      errors.minStartTime = 'Min start time is required';
    }
    if (!shiftScheduleData.startTime) {
      errors.startTime = 'Start time is required';
    }
    if (!shiftScheduleData.maxStartTime) {
      errors.maxStartTime = 'Max start time is required';
    }
    if (!shiftScheduleData.minEndTime) {
      errors.minEndTime = 'Min end time is required';
    }
    if (!shiftScheduleData.endTime) {
      errors.endTime = 'End time is required';
    }

    if (!shiftScheduleData.maxEndTime) {
      errors.maxEndTime = 'Max end time is required';
    }

    if (!shiftScheduleData.breakTime && shiftScheduleData.breakTime !== 0) {
      errors.breakTime = 'Break time is required';
    }
    if (
      shiftScheduleData.fromDate &&
      shiftScheduleData.toDate &&
      (
        shiftScheduleData.fromDate.isAfter(shiftScheduleData.toDate))
    ) {
      errors.toDate = 'To Date must be after From Date';
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    const employeeIDs = shiftScheduleData.selectEmployeeNames.map((emp: any) => emp.value);
    const shiftData = {
      EmployeeIDs: JSON.stringify(employeeIDs),
      DepartmentID: shiftScheduleData.departmentName,
      ShiftName: shiftScheduleData.selectedShift?.label,
      fromDate: shiftScheduleData.fromDate?.format("YYYY-MM-DD"),
      toDate: shiftScheduleData.toDate?.format("YYYY-MM-DD"),
      ShiftID: shiftScheduleData.selectedShift?.value,
      MinStartTime: shiftScheduleData.minStartTime?.format("HH:mm:ss"),
      StartTime: shiftScheduleData.startTime?.format("HH:mm:ss"),
      MaxStartTime: shiftScheduleData.maxStartTime?.format("HH:mm:ss"),
      MinEndTime: shiftScheduleData.minEndTime?.format("HH:mm:ss"),
      EndTime: shiftScheduleData.endTime?.format("HH:mm:ss"),
      MaxEndTime: shiftScheduleData.maxEndTime?.format("HH:mm:ss"),
      BreakTime: shiftScheduleData.breakTime?.format("HH:mm:ss"),
      AcceptExtraHours: shiftScheduleData.acceptExtraHours,
      IsPublished: shiftScheduleData.isPublished,
    };

    let result;

    if (modalType === 'create') {
      try {
        result = await dispatch(createShiftSchedule(shiftData)).unwrap();
        message.success('Shift Schedule Created Successfully!');
      } catch (err: any) {
        const errorMessage = err?.error || 'Failed to create Shift Schedule. Please try again.';
        message.error(errorMessage);
        return;
      }
    } else if (modalType === 'edit' && selectedRowData?.ScheduleID) {
      try {
        result = await dispatch(updateShiftSchedule({
          scheduleId: selectedRowData.ScheduleID,
          data: shiftData
        })).unwrap();
        message.success('Shift Schedule Updated Successfully!');
      } catch (err: any) {
        const errorMessage = err?.error || 'Failed to update Shift Schedule. Please try again.';
        message.error(errorMessage);
        return;
      }
    }

    // After processing, fetch all shift schedules
    if (result.status === "success") {
      await dispatch(fetchAllShiftSchedules());
      setTableKey((prevKey) => prevKey + 1);
      resetFields(); // Reset form fields
      setValidationErrors({});
    }
    // Close modal after submission
    handleCancel();
  };
  const handlePrint = () => {
    window.print();
  };

  const ExportScheduleData = shiftSchedules.map((schedule: any) => ({
    EmployeeNames: schedule.EmployeeNames?.[0] || "N/A",
    DepartmentName: schedule.DepartmentName || "N/A",
    ShiftName: shiftMasters.find((shift: any) => shift.ShiftID.toString() === schedule.ShiftName?.toString())?.ShiftName || schedule.ShiftName || "N/A",
    fromDate: schedule.FromDate ? new Date(schedule.FromDate).toLocaleDateString("en-IN") : "N/A",
    toDate: schedule.ToDate ? new Date(schedule.ToDate).toLocaleDateString("en-IN") : "N/A",
    MinStartTime: schedule.MinStartTime ? new Date(`1970-01-01T${schedule.MinStartTime}`).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "N/A",
    StartTime: schedule.StartTime ? new Date(`1970-01-01T${schedule.StartTime}`).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "N/A",
    MaxStartTime: schedule.MaxStartTime ? new Date(`1970-01-01T${schedule.MaxStartTime}`).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "N/A",
    MinEndTime: schedule.MinEndTime ? new Date(`1970-01-01T${schedule.MinEndTime}`).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "N/A",
    EndTime: schedule.EndTime ? new Date(`1970-01-01T${schedule.EndTime}`).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "N/A",
    MaxEndTime: schedule.MaxEndTime ? new Date(`1970-01-01T${schedule.MaxEndTime}`).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "N/A",
    BreakTime: schedule.BreakTime || "0",
    AcceptExtraHours: schedule.AcceptExtraHours ? "Yes" : "No",
  }));

  const handleOpenRosterDrawer = async () => {
    setRosterLoading(true);
    await dispatch(fetchAllShiftSchedules());
    setRosterLoading(false);
    setShowRosterDrawer(true);
  };


  const data = shiftSchedules ? shiftSchedules : [];

  useEffect(() => {
    setTableKey(prevKey => prevKey + 1);
  }, [shiftSchedules]);

  const config = useAppConfig();
  if (!config) return null;


  const columns = [
    {
      title: "Name",
      dataIndex: "EmployeeNames", // Use EmployeeNames since it's an array of names
      render: (text: string[], record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <div className="ms-2">
            <h6 className="fw-medium">
              {/* Join the employee names with a comma separator */}
              <Link to="#">{text?.join(', ')}</Link>
            </h6>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.EmployeeNames?.join(', ').length - b.EmployeeNames?.join(', ').length,
    },

    {
      title: "Department Name",
      dataIndex: "DepartmentName",
      sorter: (a: any, b: any) =>
        (a.DepartmentName || "").localeCompare(b.DepartmentName || ""),
    },
    {
      title: "Start Date",
      dataIndex: "FromDate",
      render: (text: string) => formatDate(text, config),
      sorter: (a: any, b: any) => a.ToDate.length - b.ToDate.length,
    },
    {
      title: "End Date",
      dataIndex: "ToDate",
      render: (text: string) => formatDate(text, config),
      sorter: (a: any, b: any) => a.ToDate.length - b.ToDate.length,
    },
    {
      title: "User Available Timings",
      dataIndex: "UserAvailableTimings",
      render: (text: String, record: any) => (
        <div>
          <p className="mb-0">{record.StartTime} - {record.EndTime}</p>
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div className="action-icon d-inline-flex">
          <button
            className="me-2"
            onClick={() => { if (!canEdit) return; showModal("edit"); handleEditSchedule(record); }}
            disabled={!canEdit}
            style={{ cursor: canEdit ? "pointer" : "not-allowed", opacity: canEdit ? 1 : 0.5, }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={!canEdit ? "Permission Denied" : "Edit Schedule"}
          >
            <i className="ti ti-edit" />
          </button>

          <button
            onClick={() => { if (!canDelete) return; setSelectedRowData(record); setIsDeleteModalVisible(true); }}
            className="me-2"
            disabled={!canDelete}
            style={{ cursor: canDelete ? "pointer" : "not-allowed", opacity: canDelete ? 1 : 0.5, }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={!canDelete ? "Permission Denied" : "Delete Schedule"}
          >
            <i className="ti ti-trash" />
          </button>

        </div>

      ),
    },
  ];


  const shiftsChoose = shiftMasters.map((shift: any) => ({
    value: shift.ShiftID.toString(),
    label: `${shift.ShiftName} (${shift.StartTime} - ${shift.EndTime})`
  }));

  type TimeField =
    | "minStartTime"
    | "startTime"
    | "maxStartTime"
    | "minEndTime"
    | "endTime"
    | "maxEndTime"
    | "breakTime";

  const renderTimePickerField = (field: TimeField) => {
    const labelMap: Record<TimeField, string> = {
      minStartTime: "Min Start Time",
      startTime: "Start Time",
      maxStartTime: "Max Start Time",
      minEndTime: "Min End Time",
      endTime: "End Time",
      maxEndTime: "Max End Time",
      breakTime: "Break Time",
    };
    // const isDisabled = ["startTime", "endTime", "breakTime"].includes(field);

    return (
      <div className="col-sm-4 mb-3" key={field}>
        <label className="form-label">
          {labelMap[field]} <span className="text-danger">*</span>
        </label>
        <TimePicker
          className={`form-control border-2 ${validationErrors[field] ? "is-invalid" : ""}`}
          value={shiftScheduleData[field]}
          onChange={(value) => handleInputChange(field, value)}
          format="HH:mm"
        // disabled={isDisabled}
        />
        {validationErrors[field] && (
          <div className="text-danger">{validationErrors[field]}</div>
        )}
      </div>
    );
  };

  const handleSchedulingDelete = async (scheduleId: number) => {
    try {
      const result = await dispatch(deleteShiftSchedule(scheduleId));
      if (result.meta.requestStatus === 'fulfilled') {
        message.success('Shift schedule deleted successfully');
        await dispatch(fetchAllShiftSchedules());
        setTableKey((prevKey) => prevKey + 1);
        setIsDeleteModalVisible(false);
      } else {
        message.warning(result.payload.error);
      }
    } catch (error) {
      message.error('Something went wrong');
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Schedule Timing</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Attendance</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Schedule Timing
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center">

              {/* Optional: Prev / Next week nudges */}

              <div className="me-2">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-file-export me-1" />
                    Export
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={() => exportToCSV(ExportScheduleData, 'schedule_time')}
                      >
                        <i className="ti ti-file-code me-1" />
                        Export as CSV
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={() => exportToExcel(ExportScheduleData, 'schedule_data')}
                      >
                        <i className="ti ti-file-type-xls me-1" />
                        Export as Excel{" "}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={() => exportToPDF(ExportScheduleData, 'schedule_data')}
                      >
                        <i className="ti ti-file-type-xls me-1" />
                        Export as PDF{" "}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={handlePrint}
                      >
                        <i className="ti ti-printer me-1" />
                        Print Schedule{" "}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
              <button onClick={handleOpenRosterDrawer} className="btn btn-primary d-flex align-items-center me-2">
                {rosterLoading ? <Spin size="small" style={{ marginRight: 8 }} /> : <i className="ti ti-circle-plus me-2" />}
                Roster
              </button>
              {canAdd && (
                <button onClick={() => showModal('create')} className="btn btn-primary d-flex align-items-center me-2">
                  <i className="ti ti-circle-plus me-2" />
                  Add Schedule
                </button>
              )}

              <button onClick={() => setShowRotationDrawer(true)} className="btn btn-primary d-flex align-items-center me-2">
                <i className="ti ti-refresh me-2" />
                Create Rotation
              </button>
            </div>
          </div>
          <div className='d-flex justify-content-between'>
            <div className="me-2">
              <RangePicker
                value={calendarRange}
                onChange={(vals) => handleCalendarRangeChange(vals as (Dayjs | null)[])}
                format="DD-MM-YYYY"
                allowClear={false}
                inputReadOnly
              />
            </div>
            <div className='d-flex'>
              <div className="me-2" style={{ minWidth: 260 }}>
                <Input
                  allowClear
                  className='p-2'
                  placeholder="Search employee name"
                  value={empQuery}
                  onChange={(e) => setEmpQuery(e.target.value)}
                />
              </div>

              <div className="me-2" style={{ minWidth: 220 }}>
                <select
                  className="form-control"
                  value={calendarDeptId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCalendarDeptId(v === 'all' ? 'all' : parseInt(v, 10));
                  }}
                >
                  <option value="all">All Departments</option>
                  {[...activeDepartment]
                    .sort((a: any, b: any) => String(a?.Name).localeCompare(String(b?.Name)))
                    .map((dept: any) => (
                      <option key={dept.Id} value={dept.Id}>
                        {dept.Name}
                      </option>
                    ))}
                </select>
              </div>

              <button type="button" className="btn btn-light me-2" onClick={() => shiftWeek(-1)}>
                <i className="ti ti-chevron-left me-1" /> Prev 7d
              </button>
              <button type="button" className="btn btn-light me-3" onClick={() => shiftWeek(1)}>
                Next 7d <i className="ti ti-chevron-right ms-1" />
              </button>
            </div>
          </div>

          <div className="card print-calendar">
            <div className="card-body p-0">
              <ShiftCalendar
                employees={filteredEmployeesForCalendar}
                shiftSchedules={shiftSchedules}
                startDate={calendarRange[0]}
                endDate={calendarRange[1]}
                onCellClick={handleCalendarCellClick}
                holidays={holidays}
              />
            </div>
          </div>
        </div>

        <Drawer
          title={modalType === "edit" ? "Edit Schedule" : "Add Schedule"}
          placement="right"
          width={750}
          onClose={handleCancel}
          visible={isModalVisible}
          style={{ marginTop: 50, paddingBottom: 50 }}
        >
          <form onSubmit={handleSubmit}>
            <div className='row'>

              {/* Date Picker */}
              <div className="col-sm-6 mb-3">
                <label className="form-label">
                  From-Date <span className="text-danger"> *</span>
                </label>
                <div className="input-icon-end position-relative">
                  <DatePicker
                    className={`form-control border-2 ${validationErrors.fromDate ? 'is-invalid' : ''}`}
                    format="DD-MM-YYYY"
                    value={shiftScheduleData.fromDate}
                    onChange={(date) => handleInputChange("fromDate", date)}
                  />

                  <span className="input-icon-addon">
                    <i className="ti ti-calendar text-gray-7" />
                  </span>
                </div>
                {validationErrors.fromDate && (
                  <div className="text-danger">{validationErrors.fromDate}</div>
                )}
              </div>
              <div className="col-sm-6 mb-3">
                <label className="form-label">
                  To-Date <span className="text-danger"> *</span>
                </label>
                <div className="input-icon-end position-relative">
                  <DatePicker
                    className={`form-control border-2 ${validationErrors.toDate ? 'is-invalid' : ''}`}
                    format="DD-MM-YYYY"
                    value={shiftScheduleData.toDate}
                    onChange={(date) => handleInputChange("toDate", date)}
                  />

                  <span className="input-icon-addon">
                    <i className="ti ti-calendar text-gray-7" />
                  </span>
                </div>
                {validationErrors.toDate && (
                  <div className="text-danger">{validationErrors.toDate}</div>
                )}
              </div>

            </div>
            <div className="row">
              {/* Department */}
              <div className="col-sm-6 mb-3">
                <label className="form-label">
                  Department <span className="text-danger"> *</span>
                </label>
                <select
                  className={`form-control border-2 ${validationErrors.departmentName ? 'is-invalid' : ''}`}
                  value={shiftScheduleData.departmentName}
                  onChange={(e) => handleInputChange("departmentName", e.target.value)}
                  disabled={isDepartmentLocked}
                >
                  <option value="">Select Department</option>
                  {[...activeDepartment]
                    .sort((a, b) => a.Name.localeCompare(b.Name))
                    .map((dept: any) => (
                      <option key={dept.Id} value={dept.Id}> {dept.Name} </option>
                    ))}
                </select>
                {validationErrors.departmentName && (
                  <div className="text-danger">{validationErrors.departmentName}</div>
                )}
              </div>

              {/* Employee Name */}
              <div className="col-sm-6 mb-3">
                <label className="form-label">
                  Employee Name <span className="text-danger"> *</span>
                </label>
                <Select
                  isMulti
                  options={
                    activeEmployee
                      .filter((emp: any) =>
                        emp.DepartmentId === parseInt(shiftScheduleData.departmentName) &&
                        !assignedEmployeeIDs.includes(emp.EmployeeID) // exclude already scheduled employees
                      )
                      .sort((a, b) => a.Name.localeCompare(b.Name))
                      .map((emp: any) => ({
                        value: emp.EmployeeID,
                        label: emp.Name,
                      }))
                  }
                  value={shiftScheduleData.selectEmployeeNames}
                  onChange={(selectedEmployees: any) =>
                    handleInputChange('selectEmployeeNames', selectedEmployees)
                  }
                />
                {validationErrors.selectEmployeeNames && (
                  <div className="text-danger">{validationErrors.selectEmployeeNames}</div>
                )}
              </div>

              {/* Shift Selector */}
              <div className="col-sm-6 mb-3">
                <label className="form-label">
                  Shift <span className="text-danger"> *</span>
                </label>
                <select
                  className={`form-control border-2 ${validationErrors.selectedShift ? 'is-invalid' : ''}`}
                  value={shiftScheduleData.selectedShift?.value ?? ''} // value should be ShiftID
                  onChange={(e) => {
                    const id = e.target.value;
                    const s = shiftMasters.find((x: any) => String(x.ShiftID) === String(id));
                    const opt = s
                      ? { value: String(s.ShiftID), label: `${s.ShiftName}` }
                      : { value: id, label: id };
                    handleInputChange("selectedShift", opt);
                  }}
                >
                  <option value="">Select Shift</option>
                  {shiftsChoose.map((shift: any) => (
                    <option key={shift.value} value={shift.value}>
                      {shift.label}
                    </option>
                  ))}
                </select>
                {validationErrors.selectedShift && (
                  <div className="text-danger">{validationErrors.selectedShift}</div>
                )}
              </div>

              {/* Time Pickers */}
              {(["breakTime", "minStartTime", "startTime", "maxStartTime", "minEndTime", "endTime", "maxEndTime"] as TimeField[]).map(renderTimePickerField)}

              {validationErrors.time && (
                <div className="text-danger">{validationErrors.time}</div>
              )}

              {/* Accept Extra Hours Checkbox */}
              <div className="col-sm-3 mt-3 d-flex align-items-center">
                <label className="form-label mt-1">Accept Extra Hours </label>
                <input
                  type="checkbox"
                  className="ms-2"
                  checked={shiftScheduleData.acceptExtraHours}
                  onChange={(e) => handleInputChange("acceptExtraHours", e.target.checked)}
                />
              </div>

              {/* Publish Checkbox */}
              {/* <div className="col-sm-3 mt-3 d-flex align-items-center">
                  <label className="form-label mt-1">Publish</label>
                  <input
                    type="checkbox"
                    className="ms-2"
                    checked={shiftScheduleData.isPublished}
                    onChange={(e) => handleInputChange("isPublished", e.target.checked)}
                  />
                </div> */}
            </div>

            <div className="submit-section mt-4 text-center">
              <button type="button" className="btn btn-light me-2  py-2 rounded-pill" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary px-4 py-2 rounded-pill" disabled={isLoading}>
                {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
                {modalType === "edit" ? "Update " : "Submit"}
              </button>
            </div>
          </form>

        </Drawer>
      </div>
      < DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() => selectedRowData?.ScheduleID && handleSchedulingDelete(selectedRowData.ScheduleID)}
      />
      <Drawer
        title="Create Rotation"
        placement="right"
        width={820}
        onClose={handleRotationClose}
        visible={showRotationDrawer}
        style={{ marginTop: 50, paddingBottom: 50 }}
      >
        <div className="row">

          {/* Frequency */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Frequency</label>
            <select
              className="form-control"
              value={rotationFrequency}
              onChange={e => setRotationFrequency(e.target.value as 'Weekly' | 'Monthly' | 'Daily')}
            >
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="Daily">Daily</option>
            </select>
          </div>

          {/* From Day (the anchor date) */}
          <div className="col-md-6 mb-3">
            <label className="form-label">From Day</label>
            <DatePicker
              className="form-control"
              value={rotationStartDate}
              onChange={(d) => setRotationStartDate(d)}
              format="DD-MM-YYYY"
            />
          </div>

          {/* Applicable For */}

          <div className="col-md-12 mb-3">
            <label className="form-label">Departments</label>
            <Select
              isMulti
              closeMenuOnSelect={false}
              options={[...activeDepartment]
                .sort((a: any, b: any) => a.Name.localeCompare(b.Name))
                .map((d: any) => ({ value: d.Id, label: d.Name }))}
              value={rotationDeptIds.map((id) => ({ value: id, label: activeDepartment.find((x: any) => x.Id === id)?.Name || String(id) }))}
              onChange={(vals: any) => setRotationDeptIds(vals.map((v: any) => Number(v.value)))}
            />
          </div>

          <div className="col-md-12 mb-3">
            <label className="form-label">Employees (optional)</label>
            <Select
              isMulti
              options={activeEmployee
                .filter((e: any) => rotationDeptIds.length === 0 || rotationDeptIds.includes(Number(e.DepartmentId)))
                .sort((a: any, b: any) => a.Name.localeCompare(b.Name))
                .map((e: any) => ({ value: e.EmployeeID, label: `${e.Name} (${e.EmployeeNumber || '—'})` }))}
              value={rotationEmployeeMulti}
              onChange={(vals: any) => setRotationEmployeeMulti(vals)}
              placeholder="If empty, all employees from selected departments will be used"
            />
          </div>

          {/* Rotation Pairs */}
          <div className="col-12 mb-2"><h6 className="mb-0">Shift Rotation (alternate per period)</h6></div>

          {rotationPairs.map((pair, idx) => (
            <div key={pair.id} className="col-12 mb-2 d-flex gap-2 align-items-center">
              <div style={{ flex: 1 }}>
                <select
                  className="form-control"
                  value={pair.fromShiftId || ''}
                  onChange={e => {
                    const v = e.target.value;
                    setRotationPairs(p => p.map(x => x.id === pair.id ? { ...x, fromShiftId: v } : x));
                  }}
                >
                  <option value="">From Shift</option>
                  {shiftMasters.map((s: any) => (
                    <option key={s.ShiftID} value={String(s.ShiftID)}>{s.ShiftName} ({s.StartTime} - {s.EndTime})</option>
                  ))}
                </select>
              </div>
              <span className="mx-1">→</span>
              <div style={{ flex: 1 }}>
                <select
                  className="form-control"
                  value={pair.toShiftId || ''}
                  onChange={e => {
                    const v = e.target.value;
                    setRotationPairs(p => p.map(x => x.id === pair.id ? { ...x, toShiftId: v } : x));
                  }}
                >
                  <option value="">To Shift</option>
                  {shiftMasters.map((s: any) => (
                    <option key={s.ShiftID} value={String(s.ShiftID)}>{s.ShiftName} ({s.StartTime} - {s.EndTime})</option>
                  ))}
                </select>
              </div>

              <button type="button" className="btn btn-light ms-2"
                onClick={() => { if (rotationPairs.length > 1) setRotationPairs(p => p.filter(x => x.id !== pair.id)); }}>
                <i className="ti ti-minus" />
              </button>
              {idx === rotationPairs.length - 1 && (
                <button type="button" className="btn btn-light"
                  onClick={() => setRotationPairs(p => [...p, { id: crypto.randomUUID() }])}>
                  <i className="ti ti-plus" />
                </button>
              )}
            </div>
          ))}

          {/* Actions */}
          <div className="col-12 mt-4 text-center">
            <button type="button" className="btn btn-light me-2 py-2 rounded-pill"
              onClick={handleRotationClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary px-4 py-2 rounded-pill"
              disabled={rotating}
              onClick={async () => {
                if (!rotationStartDate) { return message.warning('Pick a From Day'); }
                if (rotationDeptIds.length === 0) { return message.warning('Select at least one department'); }
                if (rotationPairs.length < 1 || rotationPairs.some(p => !p.fromShiftId || !p.toShiftId)) {
                  return message.warning('Complete rotation pairs');
                }

                setRotating(true);
                try {
                  // resolve candidate employees
                  const allChosenEmpIds = rotationEmployeeMulti.length
                    ? rotationEmployeeMulti.map(e => Number(e.value))
                    : activeEmployee
                      .filter((e: any) => rotationDeptIds.includes(Number(e.DepartmentId)))
                      .map((e: any) => Number(e.EmployeeID));

                  if (allChosenEmpIds.length === 0) {
                    message.warning('No employees found for selected departments');
                    setRotating(false); return;
                  }

                  // periods: 4 cycles (WEEKLY or MONTHLY)
                  const periods =
                    rotationFrequency === 'Weekly'
                      ? makeWeeklyPeriods(rotationStartDate)
                      : rotationFrequency === 'Monthly'
                        ? makeMonthlyPeriods(rotationStartDate)
                        : makeDailyPeriods(rotationStartDate); // Daily

                  // alternate pair1, pair2, pair1, pair2...
                  for (let i = 0; i < 4; i++) {
                    const pair = rotationPairs[i % rotationPairs.length];
                    const targetShift = getShiftById(pair.toShiftId);
                    if (!targetShift) continue;

                    for (const deptId of rotationDeptIds) {
                      const deptEmpIds = allChosenEmpIds.filter((eid: number) => {
                        const emp = activeEmployee.find((x: any) => Number(x.EmployeeID) === eid);
                        return emp && Number(emp.DepartmentId) === Number(deptId);
                      });

                      if (deptEmpIds.length === 0) continue;

                      const payload = buildSchedulePayload(targetShift, deptEmpIds, periods[i], deptId);
                      await dispatch(createShiftSchedule(payload)).unwrap();
                    }
                  }

                  message.success('Rotation schedules created');
                  await dispatch(fetchAllShiftSchedules());
                  setTableKey(k => k + 1);
                  handleRotationClose();
                } catch (err: any) {
                  message.error(err?.error || 'Failed to create rotation schedules');
                } finally {
                  setRotating(false);
                }
              }}
            >
              {rotating && <span className="spinner-border spinner-border-sm me-2" />}
              Create Rotation
            </button>
          </div>
        </div>
      </Drawer>

      <ShiftRosterDrawer showRosterDrawer={showRosterDrawer} setShowRosterDrawer={setShowRosterDrawer} holidays={holidays} />
      <style>{`
        /* Force A4 Landscape */
        @page { size: A4 landscape; margin: 10mm; }

        /* Print only the calendar area */
        @media print {
          /* Hide EVERYTHING by default */
          body * { visibility: hidden !important; }

          /* Show only the calendar wrapper and its children */
          .print-calendar, .print-calendar * { visibility: visible !important; }

          /* Lift the calendar to top-left to occupy the page */
          .print-calendar {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }

          /* If your table is native <table>, repeat header each page */
          .print-calendar thead { display: table-header-group; }
          .print-calendar tfoot { display: table-footer-group; }

          /* Keep rows from splitting across pages */
          .print-calendar tr { page-break-inside: avoid; }

          /* Slightly compact text for better fit */
          .print-calendar table { font-size: 11px; }
        }
      `}</style>
    </>
  )
}

export default ScheduleTiming;
